/**
 * FastSpring storefront resolution for the us2/Texas lane (F-285).
 *
 * AC 1: storefront ID from env (REACT_APP_FASTSPRING_STORE_ID) — no hardcode.
 * The ID itself is repo-agnostic; the concrete value is provisioned as a CI
 * secret on the us2 workflows (red-flower prod, yellow-sand staging) so the
 * store ID is baked per-build, exactly like the Cognito pool/client env vars.
 *
 * UK guard (AC 5): the storefront script and session helpers are us2-only.
 * getAppMarket() is baked at build time (REACT_APP_MARKET), so a UK build
 * (unset/`uk`) can never load or invoke the Texas storefront.
 */

import { getAppMarket } from './MarketService';

export const FASTSPRING_SBL_VERSION = '0.9.5';
const SCRIPT_ID = 'fastspring-sbl-us2';

/**
 * FastSpring demo/test mode. When true the storefront is served from the
 * `.test.` shard (`<id>.test.onfastspring.com`) — a faithful copy of the live
 * store that never charges real cards. Baked true ONLY on the staging
 * (yellow-sand) build via REACT_APP_FASTSPRING_TEST_MODE. Prod (red-flower)
 * leaves it unset so the live storefront is used; the bundle guard fails the
 * deploy if the test shard ever leaks into prod.
 */
export function isFastSpringTestMode(): boolean {
  return String(process.env.REACT_APP_FASTSPRING_TEST_MODE || '').trim().toLowerCase() === 'true';
}

/** Host for a store ID: test mode uses the `.test.` shard, prod the live host. */
export function storefrontDomain(storeId: string): string {
  return isFastSpringTestMode()
    ? `${storeId}.test.onfastspring.com`
    : `${storeId}.onfastspring.com`;
}

export type StorefrontResolution =
  | { ok: true; storeId: string; checkoutDomain: string }
  | { ok: false; code: 'FASTSPRING_UNAVAILABLE' | 'STORE_ID_MISSING' | 'STORE_ID_INVALID'; message: string };

/** Is this build a us2/Texas lane build? UK builds never see FastSpring. */
export function isUs2Lane(): boolean {
  return getAppMarket() === 'texas';
}

/** Storefront IDs are lowercased alphanumerics + hyphens (FastSpring rule). */
export function isValidStoreId(id: string): boolean {
  return /^[a-z0-9][a-z0-9-]*$/i.test(id) && id.length >= 3 && id.length <= 63;
}

export function resolveStorefront(): StorefrontResolution {
  if (!isUs2Lane()) {
    return {
      ok: false,
      code: 'FASTSPRING_UNAVAILABLE',
      message: 'FastSpring checkout is only available on the Texas market.',
    };
  }

  const envId = (process.env.REACT_APP_FASTSPRING_STORE_ID || '').trim();
  if (envId) {
    if (!isValidStoreId(envId)) {
      return {
        ok: false,
        code: 'STORE_ID_INVALID',
        message: 'Configured FastSpring store ID is malformed (check REACT_APP_FASTSPRING_STORE_ID).',
      };
    }
    return { ok: true, storeId: envId, checkoutDomain: storefrontDomain(envId) };
  }

  // Env not baked (local dev / older CI). The usd-catalog backend may expose
  // the storefront for the stage once the endpoint lands — surfaced as
  // GET /api/usd-catalog/storefront → { data: { storeId } }. Until then the
  // resolution fails closed with a clear, retryable error.
  return {
    ok: false,
    code: 'STORE_ID_MISSING',
    message:
      'FastSpring store ID is not baked into this build. Set REACT_APP_FASTSPRING_STORE_ID on the us2 CI workflow.',
  };
}

export function storefrontCheckoutUrl(storeId: string): string {
  return `https://${storefrontDomain(storeId)}/popup-${storeId}`;
}

export function sblScriptUrl(): string {
  return `https://sbl.onfastspring.com/sbl/${FASTSPRING_SBL_VERSION}/fastspring-builder.min.js`;
}

/**
 * Load the FastSpring SBL script for the resolved us2 storefront. Idempotent;
 * resolves only once the script has actually loaded (the dormant UK
 * PricingPage fire-and-forgets its loader, which races the popup call).
 * Rejects with the resolution message on any guard failure.
 */
export function loadUs2Storefront(): Promise<{ storeId: string; checkoutDomain: string }> {
  return new Promise((resolve, reject) => {
    const resolution = resolveStorefront();
    if (!resolution.ok) {
      reject(new Error(resolution.message));
      return;
    }
    const { storeId, checkoutDomain } = resolution;

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // Already loaded (or loading) for this storefront — wait for load.
      existing.addEventListener('load', () => resolve({ storeId, checkoutDomain }), { once: true });
      existing.addEventListener('error', () => reject(new Error('FastSpring checkout failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = sblScriptUrl();
    script.async = true;
    // Popup storefront: https://{id}.onfastspring.com/popup-{id}
    // (staging uses the `.test.` shard via storefrontCheckoutUrl).
    script.setAttribute('data-storefront', storefrontCheckoutUrl(storeId));
    script.setAttribute('data-popup-closed', 'onUs2FsPopupClosed');
    script.setAttribute('data-data-callback', 'onUs2FsData');
    script.setAttribute('data-error-callback', 'onUs2FsError');
    script.addEventListener('load', () => resolve({ storeId, checkoutDomain }), { once: true });
    script.addEventListener('error', () => reject(new Error('FastSpring checkout failed to load.')), { once: true });
    document.head.appendChild(script);
  });
}

/** Attach the global SBL callbacks for one pending checkout session. */
export function attachPopupSession(handlers: {
  onClosed: (orderReference: unknown) => void;
  onData: (data: unknown) => void;
  onError: (code: string, message: string) => void;
}): void {
  (window as any).onUs2FsPopupClosed = handlers.onClosed;
  (window as any).onUs2FsData = handlers.onData;
  (window as any).onUs2FsError = handlers.onError;
}

export function detachPopupSession(): void {
  delete (window as any).onUs2FsPopupClosed;
  delete (window as any).onUs2FsData;
  delete (window as any).onUs2FsError;
}

/** Open the SBL popup with the session payload the backend returns. */
export function openPopupSession(popupData: {
  products: unknown[];
  tags?: Record<string, unknown>;
  contact?: Record<string, unknown>;
}): void {
  const fs = (window as any).fastspring;
  if (!fs?.builder) {
    throw new Error('FastSpring checkout is still loading — try again in a moment.');
  }
  fs.builder.reset();
  fs.builder.push({
    products: popupData.products,
    tags: popupData.tags || {},
    contact: popupData.contact || {},
    checkout: true,
  });
}
